import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { adminGet } from "../../lib/adminApi";
import { PageHeader, ErrorBanner, OvergeslagenBanner } from "../../components/admin/AdminUI";
import { OnlineBanner } from "../../components/aanmetingen/OnlineBanner";
import { Badge, MeasureRule, StatusBadge } from "../../components/fulkra";

/**
 * Daglijst voor de aanmeter, in de huisstijl.
 *
 * Restyling, geen herbouw: dezelfde drie filters, dezelfde verversing elke 30 seconden,
 * dezelfde tellingen per status en alle data-testid's ongewijzigd.
 *
 * De aanraakvlakken blijven bewust op 48px in plaats van de gewone 36: dit scherm wordt
 * staand bediend, vaak met een tablet in de hand. Het eigenlijke tabletontwerp bestaat
 * nog niet — dat staat als open punt in de bouwopdracht en wordt hier niet verzonnen.
 */

function todayIso() {
  const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10);
}

export default function AanmeetdagenIndexPage() {
  const [filter, setFilter] = useState("vandaag");
  const params = { limit: 50 };
  if (filter === "vandaag") { params.datum_van = todayIso(); params.datum_tot = todayIso(); }
  else if (filter === "aankomend") { params.datum_van = todayIso(); }

  const q = useQuery({
    queryKey: ["aanmeetdagen-tablet", params],
    queryFn: () => adminGet("/api/admin/aanmeetdagen", params),
    refetchInterval: () => (document.hidden ? false : 30000),
  });

  const dagen = q.data?.items || q.data || [];

  return (
    <div className="fulkra" style={{ minHeight: "100vh", background: "var(--surface-page)" }}>
      <OnlineBanner />
      <div style={{ margin: "0 auto", maxWidth: 1120, padding: "var(--ruimte-8) var(--ruimte-7)" }} data-testid="aanmetingen-index-page">
        <PageHeader title="Aanmetingen" subtitle="Selecteer een aanmeetdag" />

        <div style={{ marginBottom: "var(--ruimte-7)", display: "flex", flexWrap: "wrap", gap: "var(--ruimte-4)" }} data-testid="dag-filter">
          {[["vandaag", "Vandaag"], ["aankomend", "Aankomend"], ["alle", "Alle dagen"]].map(([k, label]) => {
            const aan = filter === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                data-testid={`filter-${k}`}
                aria-pressed={aan}
                style={{
                  minHeight: 48, padding: "0 var(--ruimte-7)", borderRadius: "var(--radius)",
                  border: "1px solid " + (aan ? "var(--actie)" : "var(--border-subtle)"),
                  background: aan ? "var(--actie)" : "var(--wit)",
                  color: aan ? "#fff" : "var(--text-body)",
                  fontSize: "var(--fs-body)", fontFamily: "var(--font-tekst)",
                  fontWeight: "var(--fw-medium)", cursor: "pointer",
                  boxShadow: "var(--schaduw-1)", transition: "var(--overgang-kleur)",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        <ErrorBanner error={q.error} />
        <OvergeslagenBanner
          aantal={q.data?.overgeslagen} enkelvoud="aanmeetdag" meervoud="aanmeetdagen"
          testid="aanmeetdagen-overgeslagen"
        />

        {q.isLoading ? (
          <p style={{ fontSize: "var(--fs-body)", color: "var(--text-soft)" }} data-testid="dagen-loading">Laden…</p>
        ) : dagen.length === 0 ? (
          <p style={{ fontSize: "var(--fs-body)", color: "var(--text-soft)" }} data-testid="dagen-leeg">Geen aanmeetdagen gevonden.</p>
        ) : (
          <div style={{ display: "grid", gap: "var(--ruimte-5)", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
            {dagen.map((d) => <DagCard key={d.aanmeetdag_id} dag={d} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function DagCard({ dag }) {
  const uQ = useQuery({
    queryKey: ["uitvoeringen-dag", dag.aanmeetdag_id, "card"],
    queryFn: () => adminGet(`/api/admin/aanmeetdagen/${dag.aanmeetdag_id}/uitvoeringen`),
    refetchInterval: () => (document.hidden ? false : 30000),
  });
  const items = uQ.data?.items || uQ.data || [];
  const counts = items.reduce((acc, u) => { acc[u.status] = (acc[u.status] || 0) + 1; return acc; }, {});
  const [hover, setHover] = React.useState(false);

  return (
    <Link
      to={`/aanmetingen/dag/${dag.aanmeetdag_id}`}
      data-testid={`dag-card-${dag.aanmeetdag_id}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "block", textDecoration: "none",
        background: "var(--surface-card)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-vlak)", padding: "var(--pad-kaart)",
        boxShadow: hover ? "var(--elevation-raised)" : "var(--elevation-card)",
        transition: "box-shadow var(--duur-normaal) var(--ease-standaard)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "var(--ruimte-4)" }}>
        <h3 style={{ font: "var(--text-h3)", color: "var(--text-strong)" }}>{dag.datum}</h3>
        <StatusBadge value={dag.status} />
      </div>
      <p style={{ marginTop: "var(--ruimte-3)", fontSize: "var(--fs-body)", color: "var(--text-body)" }}>{dag.aanmeet_locatie}</p>
      <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>{dag.werkgever_naam || ""}</p>

      <MeasureRule height={10} spacing={7} style={{ marginTop: "var(--ruimte-5)" }} />

      <div style={{ marginTop: "var(--ruimte-5)", display: "flex", flexWrap: "wrap", gap: "var(--ruimte-3)" }}>
        {["gepland", "in_uitvoering", "voltooid", "no_show", "verzet"].map((s) => (
          <Badge key={s} data-testid={`dag-count-${dag.aanmeetdag_id}-${s}`}>
            {s}: <strong style={{ marginLeft: 2 }}>{counts[s] || 0}</strong>
          </Badge>
        ))}
      </div>
      {/* Deze tellingen vertellen de aanmeter hoeveel mensen hij vandaag verwacht. Een
          overgeslagen aanmeting maakt ze stil te laag; dan hoort er te staan dat het
          getal niet klopt. */}
      {uQ.data?.overgeslagen > 0 ? (
        <p data-testid={`dag-onvolledig-${dag.aanmeetdag_id}`} role="alert"
          style={{ marginTop: "var(--ruimte-4)", fontSize: "var(--fs-sm)", color: "var(--text-aandacht)" }}>
          Let op: deze tellingen zijn onvolledig — {uQ.data.overgeslagen} aanmeting
          {uQ.data.overgeslagen === 1 ? "" : "en"} kon niet worden meegeteld.
        </p>
      ) : null}
    </Link>
  );
}
